/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AssetType,
  BorrowerTypeSelection,
  EmploymentType,
  ErhlLoanPurpose,
  GIFTED_FUNDS_CONTRIBUTION_TO_PURCHASE,
  SAVINGS_CONTRIBUTION_TO_PURCHASE,
} from '@/helpers/const';
import type { ProductTypeBorrowerType } from '@/api/backend/public/product-type';
import {
  isCreditCardLiability,
  isDescriptionAsset,
  isMotorVehicleAsset,
  isOtherAsset,
  isOtherLiability,
  isPropertyAsset,
  isSalaried,
  isSelfEmployed,
  isVehicleLiability,
} from '@/helpers/typeGuards';
import { buildBorrowerGroups } from './borrower-groups';
import { buildCommercialBorrowerNote, hasNonIndividualBorrowers } from './commercial-borrower-note';
import { isPurchaseFundsPurpose } from '@/helpers/additionalFundsRequiredProperties';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { mapToApi } from '@/mappers';
import type { Applicant, LoanApplication, ErhlDetails, ErhlDrawdown, LoanDetails } from '@/types';

/**
 * Resolves application-level `borrowerType` for submit payloads.
 * Broker flow sets `applicationBorrowerType` from borrower groups; user-quote sets `borrowerTypeSelection` on Applicants step.
 */
function resolveSubmitBorrowerType(data: LoanApplication): ProductTypeBorrowerType | undefined {
  if (data.applicationBorrowerType) {
    return data.applicationBorrowerType;
  }
  switch (data.borrowerTypeSelection) {
    case BorrowerTypeSelection.INDIVIDUALS:
      return 'individual';
    case BorrowerTypeSelection.COMPANIES_TRUSTS:
      return 'company_trust';
    case BorrowerTypeSelection.BOTH:
      return 'individual_and_company_trust';
    default:
      return undefined;
  }
}

export const createApplication = (data: LoanApplication): any => {
  const jointApplication = !!data.applicants.find(
    (a) => a.type === 'secondary' && a.relationshipToPrimary === 'married_or_defacto',
  );

  const incomes = data.applicants.map((applicant) => ({
    applicantIndex: mapToApi.applicantIndex(applicant.id, data.applicants),
    income: data.financials[applicant.id].income,
  }));

  const expenses = data.applicants.map((applicant) => ({
    applicantIndex: mapToApi.applicantIndex(applicant.id, data.applicants),
    expenses: data.financials[applicant.id].expenses,
  }));

  const liabilities = data.applicants.map((applicant) => ({
    applicantIndex: mapToApi.applicantIndex(applicant.id, data.applicants),
    liabilities: data.financials[applicant.id].liabilities,
  }));

  const assets = data.applicants.map((applicant) => ({
    applicantIndex: mapToApi.applicantIndex(applicant.id, data.applicants),
    assets: data.financials[applicant.id].assets,
  }));

  const fundsAssets = [
    ...assets.flatMap((a) => a.assets.filter(isOtherAsset).map((oa) => mapToApi.fundsAssets(oa, a.applicantIndex))),
    ...assets.flatMap((a) =>
      a.assets.filter(isMotorVehicleAsset).map((mv) => mapToApi.fundsAssetMotorVehicle(mv, a.applicantIndex)),
    ),
    ...assets.flatMap((a) =>
      a.assets.filter(isDescriptionAsset).map((da) => mapToApi.fundsAssetDescription(da, a.applicantIndex)),
    ),
  ];

  // The purchase property only exists for a purchase loan purpose. On any other purpose the
  // application never shows it, so whatever a scenario or an earlier answer left behind must not
  // be submitted (JS-10505).
  const additionalFundsRequiredProperties =
    data.additionalFundsRequiredProperties &&
    isPurchaseFundsPurpose(data.additionalFundsRequiredProperties.reasonForFundsOption)
      ? mapToApi.additionalFundsRequiredProperties(data.additionalFundsRequiredProperties)
      : undefined;

  const submitBorrowerType = resolveSubmitBorrowerType(data);

  // contributions to savings (loan details question)
  // If it exists, add it on as a savings asset of the primary applicant.
  // ERHL is handled by the dedicated block below (which splits the gifted portion); skip here to avoid
  // emitting a duplicate "Savings to contribute to purchase" asset (JS-7760).
  if (data.loanDetails && data.lendingProduct !== 'erhl') {
    const adjustedSavingsAmount = Math.max(0, data.loanDetails.savingsAmount - (data.loanDetails.giftedAmount ?? 0));

    if (adjustedSavingsAmount > 0) {
      const asset = {
        applicantIndex: data.applicants.findIndex((a) => a.type === 'primary'),
        type: 'savings' as const,
        institution: SAVINGS_CONTRIBUTION_TO_PURCHASE,
        assetAmount: adjustedSavingsAmount,
        endDebtContribution: 0,
        towardsPurchase: adjustedSavingsAmount,
      };
      fundsAssets.push(asset);
    }

    if (data.loanDetails.giftedAmount > 0) {
      const giftedFundAsset = {
        applicantIndex: data.applicants.findIndex((a) => a.type === 'primary'),
        type: 'gifted_funds' as const,
        assetType: AssetType.GIFTED_FUND,
        institution: GIFTED_FUNDS_CONTRIBUTION_TO_PURCHASE,
        assetAmount: data.loanDetails.giftedAmount,
        endDebtContribution: 0,
        towardsPurchase: data.loanDetails.giftedAmount,
      };
      fundsAssets.push(giftedFundAsset);
    }
  }

  if (data.lendingProduct === 'erhl' && (data.erhlLoanPurposes ?? []).includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY)) {
    const purchaseProperty = data.incomingProperties?.[0];

    // Emit the savings and gifted assets independently — do not gate the whole block on savings, or
    // gifted funds are silently dropped when the contribution is zero/undefined. Each asset below is
    // guarded on its own positive amount.
    const adjustedSavingsAmount = Math.max(
      0,
      (purchaseProperty?.erhlSavingsAmount ?? 0) - (purchaseProperty?.erhlGiftedAmount ?? 0),
    );

    if (adjustedSavingsAmount > 0) {
      fundsAssets.push({
        applicantIndex: data.applicants.findIndex((a) => a.type === 'primary'),
        type: 'savings' as const,
        institution: SAVINGS_CONTRIBUTION_TO_PURCHASE,
        assetAmount: adjustedSavingsAmount,
        endDebtContribution: 0,
        towardsPurchase: adjustedSavingsAmount,
      });
    }

    if ((purchaseProperty?.erhlGiftedAmount ?? 0) > 0) {
      fundsAssets.push({
        applicantIndex: data.applicants.findIndex((a) => a.type === 'primary'),
        type: 'gifted_funds' as const,
        assetType: AssetType.GIFTED_FUND,
        institution: GIFTED_FUNDS_CONTRIBUTION_TO_PURCHASE,
        assetAmount: purchaseProperty?.erhlGiftedAmount ?? 0,
        endDebtContribution: 0,
        towardsPurchase: purchaseProperty?.erhlGiftedAmount ?? 0,
      });
    }
  }

  // Extract marketing fields from extended data (added at submission time)
  const extendedData = data as LoanApplication & {
    referralTypeId?: string;
    referralOtherDescription?: string;
    customerAwareOfBridgit?: string;
  };

  return {
    applicationType: data.applicationType,
    jointApplication,
    propertiesIntendToBuy: data.incomingProperties.length,
    propertiesIntendToSell: data.outgoingProperties.length,
    numberOfApplicants: data.applicants.length,

    structuralVersionNumber: data.structuralVersionNumber,

    // Applicants
    applicants: data.applicants.map((app) => {
      const mapped = mapToApi.applicant(app, data.financials[app.id]);

      if (data.lendingProduct !== 'erhl') return mapped;

      const erhlExtensions: Record<string, unknown> = {};

      if (data.erhlNextOfKin?.[app.id]?.length) {
        erhlExtensions.nextOfKin = data.erhlNextOfKin[app.id].map(mapToApi.nextOfKin);
      }

      const retirementDeclarations = mapToApi.retirementDetailsToDeclarations(
        data.financials[app.id]?.retirementDetails,
      );
      const expenseDeclarations = mapToApi.expenseDetailsToDeclarations(data.financials[app.id]?.expenses);
      const financialObligationsDeclarations = mapToApi.financialObligationsToDeclarations(data.financials[app.id]);
      const allDeclarations = [...retirementDeclarations, ...expenseDeclarations, ...financialObligationsDeclarations];
      if (allDeclarations.length) {
        erhlExtensions.declarations = allDeclarations;
      }

      return Object.keys(erhlExtensions).length > 0 ? { ...mapped, ...erhlExtensions } : mapped;
    }),

    // Properties
    incomingProperties: data.incomingProperties.map(mapToApi.incomingProperty),
    outgoingProperties: data.outgoingProperties.map(mapToApi.outgoingProperty),
    supportingProperties: (data.supportingProperties ?? []).map(mapToApi.supportingProperty),
    propertiesOwnership: mapToApi.propertyOwnership(data),

    // Income and Expenses
    fundsSalaried: incomes.flatMap(
      (i) => i.income?.employment.filter(isSalaried).map((s) => mapToApi.fundsSalaried(s, i.applicantIndex)) ?? [],
    ),
    fundsSelfEmployed: incomes.flatMap(
      (i) =>
        i.income?.employment.filter(isSelfEmployed).map((s) => mapToApi.fundsSelfEmployed(s, i.applicantIndex)) ?? [],
    ),
    fundsAdditionalIncome: data.applicants.flatMap((applicant) =>
      mapToApi.fundsAdditionalIncome(
        data.financials[applicant.id].income,
        mapToApi.applicantIndex(applicant.id, data.applicants),
      ),
    ),
    incomeExpectations: incomes.flatMap((i) => mapToApi.incomeExpectation(i.income, i.applicantIndex)),
    expenseExpectations: expenses.flatMap((e) => mapToApi.expenseExpectation(e.expenses, e.applicantIndex)),
    employmentDetails: incomes.flatMap((i) => mapToApi.employmentDetail(i.income, i.applicantIndex)),
    fundsExpenses: data.applicants.flatMap((applicant) =>
      mapToApi.fundsExpenses(data.financials[applicant.id], mapToApi.applicantIndex(applicant.id, data.applicants)),
    ),

    // Assets and Liabilities
    fundsAssets,
    assetsProperties: assets.flatMap((a) =>
      a.assets.filter(isPropertyAsset).map((pa) => mapToApi.assetProperty(pa, a.applicantIndex)),
    ),
    liabilitiesCards: liabilities.flatMap((l) =>
      l.liabilities.filter(isCreditCardLiability).map((cl) => mapToApi.liabilityCard(cl, l.applicantIndex)),
    ),
    liabilitiesVehicles: liabilities.flatMap((l) =>
      l.liabilities.filter(isVehicleLiability).map((vl) => mapToApi.liabilityVehicle(vl, l.applicantIndex)),
    ),
    liabilitiesOthers: liabilities.flatMap((l) =>
      l.liabilities.filter(isOtherLiability).map((ol) => mapToApi.liabilityOther(ol, l.applicantIndex)),
    ),

    existingApplications: [false],
    loanType: data.lendingProduct === 'erhl' ? 'buy_now' : data.loanType,
    lendingProduct: data.lendingProduct,
    cancelPriorApplications: data.cancelPriorApplications ?? false,

    // Additional funds required
    additionalFundsRequired: data.additionalFundsRequired,
    additionalFundsRequiredProperties,

    // Loan details
    loanDate: data.loanDetails?.loanDate,
    additionalFunds:
      data.lendingProduct === 'erhl' && (data.erhlLoanPurposes ?? []).includes(ErhlLoanPurpose.PURCHASE_NEW_PROPERTY)
        ? mapToApi.additionalFunds({
            savingsAmount: data.incomingProperties?.[0]?.erhlSavingsAmount ?? 0,
            giftedAmount: data.incomingProperties?.[0]?.erhlGiftedAmount ?? 0,
          } as LoanDetails)
        : mapToApi.additionalFunds(data.loanDetails),
    requestedLoanAmount: 0,
    brokerFeeIncludingGST: data.brokerFeeIncludingGST ?? 0,
    summaryNotes: data.summaryNotes,

    // Analytics
    utmMetrics: data.utmMetrics,

    // Marketing fields (at body level for backend compatibility)
    ...(extendedData.referralTypeId && { referralTypeId: extendedData.referralTypeId }),
    ...(extendedData.referralOtherDescription && { referralOtherDescription: extendedData.referralOtherDescription }),
    ...(extendedData.customerAwareOfBridgit && { customerAwareOfBridgit: extendedData.customerAwareOfBridgit }),

    applicationDraftId: data.applicationDraftId,
    scenarioApplicationId: data.scenarioApplicationId,

    loanTermMonths: data.loanTermMonths,
    productType: data.productType,
    ...(submitBorrowerType ? { borrowerType: submitBorrowerType } : {}),

    // Erhl Details
    ...(data.lendingProduct === 'erhl'
      ? {
          erhlDetails: {
            exitStrategy: data.exitStrategy as ErhlDetails['exitStrategy'],

            loanPurposes: data.erhlLoanPurposes,
            loanPurposeOtherDescription: data.erhlLoanPurposeOtherDescription,
            drawdowns: (data.erhlDrawdowns ?? []).map((d: ErhlDrawdown) => ({
              drawdownType: d.drawdownType,
              effectiveFrom: d.effectiveFrom,
              effectiveTo: d.effectiveTo,
              amount: d.amount,
              durationTotalMonths: d.durationTotalMonths,
              fundsRequiredBy: d.fundsRequiredBy,
              paymentFrequency: d.paymentFrequency,
            })),
          } satisfies ErhlDetails,
          exitStrategyDetails: data.exitStrategyDetails ?? '',
        }
      : {
          exitStrategy: data.exitStrategy,
          exitStrategyDetails: data.exitStrategyDetails,
        }),

    // Structured company/trust borrower groups. The backend creates real Company/Trust Borrower
    // records + role-tagged people from this, and suppresses the commercialBorrowerData note when it
    // is present. commercialBorrowerData is still sent as a transition fallback so no data
    // is lost if this frontend reaches a backend that predates the structured contract; once the
    // backend is deployed everywhere, the note builder and field below can be retired.
    //
    // Gated on the platform-wide borrower-entity rollout flag, the same flag the CRM gates its
    // borrower/contact views on. Flag off means note-only, i.e. current production behaviour.
    borrowerGroups: useFeatureFlags().isContactPageRedesignEnabled() ? buildBorrowerGroups(data) : undefined,
    commercialBorrowerData:
      hasNonIndividualBorrowers(data) || data.borrowerTypeSelection ? buildCommercialBorrowerNote(data) : undefined,

    // Company/Trust net profit for calcs net surplus calculation
    ...mapToApi.companyNetProfit(data),
  };
};

const mapApplicant = (data: LoanApplication, applicant: Applicant) => {
  const applicantIndex = mapToApi.applicantIndex(applicant.id, data.applicants);

  const numberOfDependants = data.financials[applicant.id]?.expenses?.numberOfDependants ?? 0;
  const income = data.financials[applicant.id].income;
  const fundsSalaried =
    income?.employment.filter(isSalaried).map((s) => mapToApi.fundsSalaried(s, applicantIndex)) ?? [];
  const fundsSelfEmployed =
    income?.employment.filter(isSelfEmployed).map((s) => mapToApi.fundsSelfEmployed(s, applicantIndex)) ?? [];
  const fundsAdditionalIncome = mapToApi.fundsAdditionalIncome(data.financials[applicant.id].income, applicantIndex);
  const incomeExpectations = mapToApi.incomeExpectation(income, applicantIndex);
  const expenseExpectations = mapToApi.expenseExpectation(data.financials[applicant.id].expenses, applicantIndex);
  const employmentDetails = mapToApi.employmentDetail(income, applicantIndex);

  const fundsExpenses = mapToApi.fundsExpenses(data.financials[applicant.id], applicantIndex);

  const assets = data.financials[applicant.id].assets;
  const fundsAssets = [
    ...assets.filter(isOtherAsset).map((oa) => mapToApi.fundsAssets(oa, applicantIndex)),
    ...assets.filter(isMotorVehicleAsset).map((mv) => mapToApi.fundsAssetMotorVehicle(mv, applicantIndex)),
    ...assets.filter(isDescriptionAsset).map((da) => mapToApi.fundsAssetDescription(da, applicantIndex)),
  ];
  const assetsProperties = assets.filter(isPropertyAsset).map((pa) => mapToApi.assetProperty(pa, applicantIndex));
  const propertiesOwnership = mapToApi.applicantDeclarationPropertyOwnership(data, assets);
  const liabilities = data.financials[applicant.id].liabilities;
  const liabilitiesCards = liabilities
    .filter(isCreditCardLiability)
    .map((cl) => mapToApi.liabilityCard(cl, applicantIndex));
  const liabilitiesVehicles = liabilities
    .filter(isVehicleLiability)
    .map((vl) => mapToApi.liabilityVehicle(vl, applicantIndex));
  const liabilitiesOthers = liabilities
    .filter(isOtherLiability)
    .map((ol) => mapToApi.liabilityOther(ol, applicantIndex));

  const isUnemployed = data.financials[applicant.id].income?.employment.some(
    (emp) => emp.type === EmploymentType.UNEMPLOYED,
  );

  const isRetired = data.financials[applicant.id].income?.employment.some((emp) => emp.type === EmploymentType.RETIRED);

  return {
    fundsSalaried,
    fundsSelfEmployed,
    fundsAdditionalIncome,
    incomeExpectations,
    expenseExpectations,
    employmentDetails,
    fundsExpenses,
    fundsAssets,
    assetsProperties,
    propertiesOwnership,
    liabilitiesCards,
    liabilitiesVehicles,
    liabilitiesOthers,
    numberOfDependants,
    id: applicant.id,
    isUnemployed,
    isRetired,
  };
};

export const updateApplication = (data: LoanApplication, applicant?: Applicant): any => {
  const applicants = data.applicants;

  if (applicant) {
    return mapApplicant(data, applicant);
  } else {
    const requestData = [];
    for (const mappedApplicant of applicants) {
      requestData.push(mapApplicant(data, mappedApplicant));
    }
    return requestData;
  }
};
